// @ts-nocheck
import useProductContext from "@store/Framework/Store/Data/Products/ProductsHooks/useProductContext";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Modal from "@insite/mobius/Modal";
import { getExtendedCarouselStyles } from "@store/Framework/Common/Utilities/CarouselWidgetHelpers";
import ZoomImage from "@store/Library/Components/Bunzl/ZoomImage";
import { ModalPresentationProps } from "@store/MobiusOverrides/Modal/Modal";
import Carousel, { CarouselSlide } from "@store/Overrides/Components/Carousel";
import {
    OwnProps,
    ProductDetailsPrimaryImageStyles,
} from "@store/Overrides/Widgets/ProductDetails/ProductDetailsPrimaryImage";
import React, { FC, useMemo } from "react";
import { css } from "styled-components";

const getModalStyles = (
    modalStyles: ModalPresentationProps | undefined,
    maxHeight: string,
    maxWidth: string,
): ModalPresentationProps => {
    if (!modalStyles) {
        return {};
    }
    return {
        ...modalStyles,
        cssOverrides: {
            ...modalStyles.cssOverrides,
            modalBody: css`
                &&& {
                    width: auto;
                    height: auto;
                    max-width: ${maxWidth};
                    max-height: ${maxHeight};
                }
            `,
        },
    };
};
interface Props {
    selectedImageIndex: number;
    onImageChange: (index: number) => void;
    fields: OwnProps["fields"];
    isModalOpen: boolean;
    setIsModalOpen: (isOpen: boolean) => void;
    productDetailsStyles: ProductDetailsPrimaryImageStyles;
    isMobile: boolean;
}

const PrimaryDetailsZoomModal: FC<Props> = ({
    selectedImageIndex,
    onImageChange,
    fields,
    productDetailsStyles,
    isModalOpen,
    setIsModalOpen,
    isMobile,
}) => {
    const productContext = useProductContext();
    const { images } = productContext.product;

    const extendedCarouselStyles = useMemo(() => getExtendedCarouselStyles(fields), [fields]);

    const { zoomModalHeight, zoomModalWidth, enableZoomModal } = fields;
    const modalStyles = useMemo(
        () => getModalStyles(productDetailsStyles.fullPrimaryImageModal, zoomModalHeight, zoomModalWidth),
        [productDetailsStyles.fullPrimaryImageModal, zoomModalHeight, zoomModalWidth],
    );
    const modalSlides: CarouselSlide[] | undefined = useMemo(
        () =>
            images?.map(image => ({
                id: image.id,
                renderComponent: () => (
                    <StyledWrapper key={image.id}>
                        <ZoomImage
                            maxHeight={fields.zoomModalHeight}
                            maxWidth={fields.zoomModalWidth}
                            alt={image.imageAltText}
                            handleClose={() => setIsModalOpen(false)}
                            src={image.largeImagePath}
                        />
                    </StyledWrapper>
                ),
            })),
        [images?.length, fields.zoomModalHeight, fields.zoomModalWidth],
    );
    if (!enableZoomModal || isMobile || !images || !modalSlides) {
        return null;
    }

    const image = images?.[0] ?? {
        id: productContext.product.id,
        imageAltText: productContext.product.imageAltText,
        imageType: "Static",
        largeImagePath: productContext.product.largeImagePath,
        mediumImagePath: productContext.product.mediumImagePath,
        smallImagePath: productContext.product.smallImagePath,
    };
    return (
        <>
            <Modal
                {...modalStyles}
                closeOnScrimClick={true}
                closeOnEsc={true}
                isCloseable={false}
                isOpen={isModalOpen}
                handleClose={() => setIsModalOpen(false)}
            >
                {images.length <= 1 ? (
                    <ZoomImage
                        maxHeight={zoomModalHeight}
                        maxWidth={zoomModalWidth}
                        alt={image.imageAltText}
                        handleClose={() => setIsModalOpen(false)}
                        src={image.largeImagePath}
                    />
                ) : (
                    <Carousel
                        id="productDetailsModalCarousel"
                        slides={modalSlides}
                        showDots={false}
                        maxNumberOfColumns={1}
                        startIndex={selectedImageIndex}
                        slideChangeCallback={onImageChange}
                        extendedStyles={extendedCarouselStyles}
                    />
                )}
            </Modal>
        </>
    );
};

export default PrimaryDetailsZoomModal;
